import * as React from 'react';
import * as _ from 'lodash';
import { Redirect } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  ControlState,
  ControlStyleClass,
  ControlValidation,
  DisplayData,
  Notification,
  Logging,
  renderCheckboxWithHeading,
  renderEdit,
  renderSelect,
  renderDateRange,
  ExemptionType,
  Jurisdiction,
  API,
  CertificateReport,
  NotificationData,
  ResponseError,
  CertificateReportStatus,
  UserInfo,
  UserContext,
  formatDate,
  DateFormatEnum,
  Customer,
  CustomerSummary,
  SearchAhead,
  Footer,
  VertexLink,
} from '@vcd-ecm-ui/Common';
import { VtxButton, VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import CertificateReportProps from './CertificateReportProps.interface';
import '../common-reports.scss';
import './certificate-reports.scss';
import useJurisdictions from '../Common/useJurisdictions';
import useExemptions from '../Common/useExemptions';
import useCustomers from '../Common/useCustomers';

const controlStyle: ControlStyleClass = null;

const controlStateEditable: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

const controlStateDisabled: ControlState = {
  editable: false,
  disabled: true,
  readOnly: true,
};

const controlValidation: ControlValidation = {
  requiredFields: ['reportName'],
  customMessages: [],
};

const CertificateReports = ({
  gatewayEndPoint,
}: CertificateReportProps): JSX.Element => {
  const userContext: UserInfo = React.useContext(UserContext);

  const dispatch = useDispatch();

  const [notification, setNotification] = React.useState(null);
  const [redirect, setRedirect] = React.useState(null);

  const [searchCustomerCode, setSearchCustomerCode] = React.useState(null);
  const [customers, searchingCustomers, customersNotification] = useCustomers(gatewayEndPoint, searchCustomerCode);
  const [exemptionsTypes, exemptionTypeNotification] = useExemptions(gatewayEndPoint);
  const [jurisdictions, jurisdictionNotification] = useJurisdictions(gatewayEndPoint);

  const [reportName, setReportName] = React.useState(null);
  const [jurisdiction, setJurisdiction] = React.useState(null);
  const [jurisdictionLevel, setJurisdictionLevel] = React.useState(false);
  const [customerCode, setCustomerCode] = React.useState(null);
  const [exemptionType, setExemptionType] = React.useState(null);
  const [certificateStatus, setCertificateStatus] = React.useState(null);
  const [startDateFrom, setStartDateFrom] = React.useState(null);
  const [startDateTo, setStartDateTo] = React.useState(null);
  const [expirationDateFrom, setExpirationDateFrom] = React.useState(null);
  const [expirationDateTo, setExpirationDateTo] = React.useState(null);
  const [endDateFrom, setEndDateFrom] = React.useState(null);
  const [endDateTo, setEndDateTo] = React.useState(null);

  const getCustomers = (): DisplayData[] => {
    if (customers) {
      // eslint-disable-next-line arrow-body-style
      return customers.map((item: CustomerSummary) => {
        return {
          data: item.customerCode,
          display: item.customerCode,
        };
      });
    }

    return [];
  };

  const searchCustomers = (searchValue: string) => {
    Logging.Debug('Searching Customers', searchValue);
    setSearchCustomerCode(searchValue);
  };

  const getJurisdictions = (): DisplayData[] => {
    if (jurisdictions) {
      // eslint-disable-next-line arrow-body-style
      return jurisdictions.map((item: Jurisdiction) => {
        return {
          data: item.id.toString(),
          display: item.name,
        };
      });
    }

    return [];
  };

  const getJurisdiction = (): Jurisdiction => {
    if (jurisdiction) {
      const selectedJurisdictions: Jurisdiction[] = jurisdictions.filter((item: Jurisdiction) => item.id.toString() === jurisdiction);

      if (selectedJurisdictions.length === 1) {
        return selectedJurisdictions[0];
      }
    }

    return null;
  };

  const getExemptionTypes = (): DisplayData[] => {
    if (exemptionsTypes) {
      // eslint-disable-next-line arrow-body-style
      return exemptionsTypes.map((item: ExemptionType) => {
        return {
          data: item.id.toString(),
          display: item.name,
        };
      });
    }

    return [];
  };

  const getExemptionType = (): ExemptionType => {
    if (exemptionType) {
      const selectedExemptionTypes: ExemptionType[] = exemptionsTypes.filter((item: ExemptionType) => item.id.toString() === exemptionType);

      if (selectedExemptionTypes.length === 1) {
        return selectedExemptionTypes[0];
      }
    }

    return null;
  };

  const getCertificateStatus = (): DisplayData[] => {
    const list: DisplayData[] = [
      {
        display: 'All',
        data: null,
      },
      {
        display: 'Approved',
        data: 'APPROVED',
      },
      {
        display: 'Rejected',
        data: 'REJECTED',
      },
      {
        display: 'Pending',
        data: 'PENDING',
      },
      {
        display: 'Draft',
        data: 'DRAFT',
      },
    ];

    return list;
  };

  // Make the API Call to Add the Certificate Report
  const createCertificateReport = (certificateReport: CertificateReport) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Add the Certificate Report
      await api.report.addCertificateReportV41(certificateReport);

      // Direct back to Certificate Reports
      setRedirect('/reports/certificates');
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Certificate Report Save Error',
        description: `Unable to Create the Certificate Report '${certificateReport.reportName}'.`,
        error: error as ResponseError,
      };

      setNotification(notificationData);
    }
  };

  const onCreateCertificateReport = (): void => {
    const certificateReport: CertificateReport = {
      reportName,
      status: CertificateReportStatus.Queued,
      createdBy: userContext.givenName,
      jurisdiction: getJurisdiction(),
      includeLowerJurisdictions: jurisdictionLevel,
      exemption: getExemptionType(),
      startDateRange: {
        beginDate: formatDate(startDateFrom, { dateFormat: DateFormatEnum.YMD, dateSeperator: '-' }),
        endDate: formatDate(startDateTo, { dateFormat: DateFormatEnum.YMD, dateSeperator: '-' }),
      },
      expirationDateRange: {
        beginDate: formatDate(expirationDateFrom, { dateFormat: DateFormatEnum.YMD, dateSeperator: '-' }),
        endDate: formatDate(expirationDateTo, { dateFormat: DateFormatEnum.YMD, dateSeperator: '-' }),
      },
      endDateRange: {
        beginDate: formatDate(endDateFrom, { dateFormat: DateFormatEnum.YMD, dateSeperator: '-' }),
        endDate: formatDate(endDateTo, { dateFormat: DateFormatEnum.YMD, dateSeperator: '-' }),
      },
      certificateStatus,
      customerCode,
    };

    Logging.Info('Create Certificate Report', certificateReport);

    dispatch(createCertificateReport(certificateReport));
  };

  // Notification Error was Closed
  const onCloseNotification = (): void => {
    setNotification(null);
  };

  const validateValues = (): boolean => {
    if (!reportName) {
      return false;
    }

    if ((startDateFrom && !startDateTo) || (!startDateFrom && startDateTo)) {
      return false;
    }

    if ((expirationDateFrom && !expirationDateTo) || (!expirationDateFrom && expirationDateTo)) {
      return false;
    }

    if ((endDateFrom && !endDateTo) || (!endDateFrom && endDateTo)) {
      return false;
    }

    if ((startDateFrom && startDateTo && expirationDateFrom && expirationDateTo)) {
      if (expirationDateFrom < startDateFrom) {
        return false;
      }

      if (expirationDateTo < startDateTo) {
        return false;
      }
    }

    if ((startDateFrom && startDateTo && endDateFrom && endDateTo)) {
      if (endDateFrom < startDateFrom) {
        return false;
      }

      if (endDateTo < startDateTo) {
        return false;
      }
    }

    return true;
  };

  React.useEffect(() => {
    setNotification(jurisdictionNotification || exemptionTypeNotification || customersNotification);
  }, [jurisdictionNotification, exemptionTypeNotification, customersNotification]);

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Debug('Certificate Report: Mount');

    return (): void => {
      // This Code will run once the component unmounts.
      setRedirect(null);
    };
  }, []);

  React.useEffect(() => {
    // Reset the Jurisdiction Level if Jurisdiction is not selected
    setJurisdictionLevel(jurisdiction ? jurisdictionLevel : false);
  }, [jurisdiction]);

  const Spacer = () => <span style={{ marginRight: '10px' }} />;

  if (redirect) {
    return (
      <Redirect to={redirect} />
    );
  }

  // Clear the Custom Messages
  controlValidation.customMessages = [];

  if ((startDateFrom && !startDateTo) || (!startDateFrom && startDateTo)) {
    controlValidation.customMessages = [...controlValidation.customMessages,
      { field: 'startDateRange', message: 'Both dates must be entered', type: 'Error' }];
  }

  if ((expirationDateFrom && !expirationDateTo) || (!expirationDateFrom && expirationDateTo)) {
    controlValidation.customMessages = [...controlValidation.customMessages,
      { field: 'expirationDateRange', message: 'Both dates must be entered', type: 'Error' }];
  }

  if ((endDateFrom && !endDateTo) || (!endDateFrom && endDateTo)) {
    controlValidation.customMessages = [...controlValidation.customMessages,
      { field: 'endDateRange', message: 'Both dates must be entered', type: 'Error' }];
  }

  if ((startDateFrom && startDateTo && expirationDateFrom && expirationDateTo)) {
    if (expirationDateFrom < startDateFrom) {
      controlValidation.customMessages = [...controlValidation.customMessages,
        { field: 'expirationDateRange', message: 'Expiration \'From Date\' must be on or after Start \'From Date\'', type: 'Error' }];
    }

    if (expirationDateTo < startDateTo) {
      controlValidation.customMessages = [...controlValidation.customMessages,
        { field: 'expirationDateRange', message: 'Expiration \'To Date\' must be on or after Start \'To Date\'', type: 'Error' }];
    }
  }

  if ((startDateFrom && startDateTo && endDateFrom && endDateTo)) {
    if (endDateFrom < startDateFrom) {
      controlValidation.customMessages = [...controlValidation.customMessages,
        { field: 'endDateRange', message: 'End \'From Date\' must be on or after Start \'From Date\'', type: 'Error' }];
    }

    if (endDateTo < startDateTo) {
      controlValidation.customMessages = [...controlValidation.customMessages,
        { field: 'endDateRange', message: 'End \'To Date\' must be on or after Start \'To Date\'', type: 'Error' }];
    }
  }

  const canSave: boolean = validateValues();
  // If End Date Range is configured, Expiration Date Range cannot be configured
  const endDateRangeEnabled = !(expirationDateFrom || expirationDateTo);
  // If Expiration Date Range is configured, End Date Range cannot be configured
  const expirationDateRangeEnabled = !(endDateFrom || endDateTo);

  const hideEndDateRange = false;
  const jurisdictionSelected: boolean = jurisdiction !== null;
  const searchCustomerCodeAhead: SearchAhead = {
    searching: searchingCustomers,
    searchingText: 'Searching Customers ...',
    noResultsText: 'No Customers found',
    onSearch: searchCustomers,
    debounceMilliseconds: 750,
  };

  return (
    <>
      <div className="reportsContainer">
        <div className="reportsHeaderBack">
          <VertexLink href="#/reports/certificates" display="Back to Certificate Reports" />
        </div>
        <div className="reportsHeader">
          <div className="reportsHeaderLeft">
            Generate a New Certificate Report
          </div>
        </div>
        <hr />
        <div className="reportsContentControls">
          {renderEdit(25, 'reportName', 'Report Name*', 'text', 'Report Name',
            reportName, setReportName, controlStateEditable, controlStyle, controlValidation)}
          {renderSelect(25, 'jurisdiction', 'Jurisdiction', 'Select a Jurisdiction', false, false,
            getJurisdictions(), true, null, jurisdiction, setJurisdiction, controlStateEditable, controlStyle, controlValidation)}
          {renderCheckboxWithHeading(25, 'jurisdictionLevel', 'Jurisdiction Level', 'Include all lower jurisdictions', jurisdictionLevel,
            setJurisdictionLevel, jurisdictionSelected ? controlStateEditable : controlStateDisabled, controlStyle, controlValidation)}
          <div className="field25" />
          {renderSelect(25, 'customerCode', 'Customer Code', 'Search by Customer Code', false, false,
            getCustomers(), true, searchCustomerCodeAhead, customerCode, setCustomerCode, controlStateEditable, controlStyle, controlValidation)}
          {renderSelect(25, 'exemptionType', 'Exemption Type', 'Select an Exemption Type', false, false,
            getExemptionTypes(), true, null, exemptionType, setExemptionType, controlStateEditable, controlStyle, controlValidation)}
          {renderSelect(25, 'certificateStatus', 'Certificate Status', 'Select a Certificate Status', false, false,
            getCertificateStatus(), true, null, certificateStatus, setCertificateStatus, controlStateEditable, controlStyle, controlValidation)}
          <div className="field25" />
          {renderDateRange(25, 'startDateRange', 'Start Date Range', 'Select Start Date Range', startDateFrom,
            setStartDateFrom, startDateTo, setStartDateTo, controlStateEditable, controlStyle, controlValidation)}
          {renderDateRange(25, 'expirationDateRange', 'Expiration Date Range', 'Select Expiration Date Range', expirationDateFrom,
            setExpirationDateFrom, expirationDateTo, setExpirationDateTo, expirationDateRangeEnabled ? controlStateEditable : controlStateDisabled,
            controlStyle, controlValidation)}
          {
            hideEndDateRange
            && (
              renderDateRange(25, 'endDateRange', 'End Date Range', 'Select End Date Range', endDateFrom, setEndDateFrom, endDateTo, setEndDateTo,
                endDateRangeEnabled ? controlStateEditable : controlStateDisabled, controlStyle, controlValidation)
            )
          }
        </div>
        <Footer cancelLink="#/reports/certificates" saveEnabled={canSave} onSave={onCreateCertificateReport} saveText="Generate Certificate Report" />
        <Notification
          data={notification}
          onNotificationClose={onCloseNotification}
          autoClose
        />
      </div>
    </>
  );
};

export default CertificateReports;
